import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { InputProps } from '@fhs-legacy/universal-components';
import { AutocompleteOptionsDropdown } from 'components/ucl/autocomplete-options-dropdown';
import useLocationPredictions from 'hooks/geolocation/use-location-predictions';
import useDebounceEffect from 'hooks/use-debounce-effect';
import { CustomEventNames, EventTypes, useCRMEventsContext } from 'state/crm-events';

type BaseInputProps = Pick<InputProps, 'value'>;

export interface IAutocompleteAddressInputProps extends BaseInputProps {
  testID: string;
  autoFocus?: boolean;
  label: string;
  accessibilityLabel?: string;
  /** When `fieldLabelledBy` is set, used as the field name on native (web uses `aria-labelledby` on the heading). */
  accessibilityLabelWhenLabelledByNative?: string;
  /** `nativeID` of a heading that labels this field on web (`aria-labelledby`). */
  fieldLabelledBy?: string;
  /** `nativeID` of helper text (`aria-describedby` / `accessibilityDescribedBy`). */
  fieldDescribedBy?: string;
  accessibilityHint?: string;
  addressesOnly?: boolean;
  required: boolean;

  message?: string;
  setMessage?: React.Dispatch<React.SetStateAction<string>>;

  onAddressSelected: (address: string, placeId?: string) => void;
  onInputValueChange: (inputValue: string | undefined) => void;
  onWebInputTab?: (event: KeyboardEvent) => void;
  onBlur?(): void;
}

export const AutocompleteAddressInput = React.forwardRef<
  HTMLInputElement,
  IAutocompleteAddressInputProps & { initialFocus?: boolean }
>(
  (
    {
      testID,
      label,
      accessibilityLabel,
      accessibilityLabelWhenLabelledByNative,
      fieldLabelledBy,
      fieldDescribedBy,
      accessibilityHint,
      message,
      addressesOnly = false,
      onAddressSelected = () => {},
      onInputValueChange,
      onWebInputTab,
      onBlur,
      setMessage,
      initialFocus = false,
      value = '',
    },
    ref
  ) => {
    const { formatMessage } = useIntl();
    const { logRBIEvent } = useCRMEventsContext();

    /**
     * Retrieve the predictions based on the user's entry and map them to the format that the component prop expects.
     * Only pass on the first 5 predictions.
     */
    const predictions = useLocationPredictions(value, { addressesOnly })?.slice(0, 5);

    const handleDropdownValueSelected = useCallback(
      ({ address, placeId }: any) => {
        onAddressSelected(address, placeId);
        logRBIEvent({
          name: CustomEventNames.MANUAL_LOCATION_SEARCH,
          type: EventTypes.Search,
          attributes: {
            searchInput: value,
            selectedAddress: address,
          },
        });
      },
      [logRBIEvent, onAddressSelected, value]
    );

    const handleInputValueChange = useCallback(
      (inputValue: string | undefined) => {
        onInputValueChange(inputValue || '');

        if (setMessage) {
          setMessage('');
        }
      },
      [onInputValueChange, setMessage]
    );

    useDebounceEffect(
      () => {
        onInputValueChange(value);
      },
      500,
      [value]
    );

    return (
      <AutocompleteOptionsDropdown
        noPredictionsFoundText={formatMessage({ id: 'noLocationsMatchYourSearch' })}
        testID={testID}
        onSelection={handleDropdownValueSelected}
        predictions={predictions}
        onInputChange={handleInputValueChange}
        onWebInputTab={onWebInputTab}
        onBlur={onBlur}
        value={value}
        label={label}
        accessibilityLabel={accessibilityLabel}
        accessibilityLabelWhenLabelledByNative={accessibilityLabelWhenLabelledByNative}
        fieldLabelledBy={fieldLabelledBy}
        fieldDescribedBy={fieldDescribedBy}
        accessibilityHint={accessibilityHint}
        errorMessage={message}
        ref={ref}
        clearTextIconTitle={formatMessage({ id: 'clearText' })}
        initialFocus={initialFocus}
      />
    );
  }
);

export default AutocompleteAddressInput;
